import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { PageTitleWithHelp } from "@/components/ui/title-help";
import { Button } from "@/components/ui/Button";
import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { ZplPrinterListStatus } from "@/components/ZplPrinterSelect";
import { ApiError } from "@/lib/api";
import { fetchLaborOpenSession } from "@/lib/labor/laborApi";
import { laborPageKeysEquivalent } from "@/lib/labor/laborPageKey";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";

import { clampQcLaborPageKeyForJobSlug } from "./clampQcLaborClockState";
import {
  printClampQcFailContainers,
  type ClampQcFailContainerPrint,
  type ClampQcFinishResponse,
} from "./clampQcFinishApi";
import { runClampQcReceiveOnce } from "./clampQcReceiveRun";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function waitUntilJobClockIsClosed(slug: string): Promise<void> {
  const jobPage = clampQcLaborPageKeyForJobSlug(slug);
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const payload = await fetchLaborOpenSession();
    const page = payload.session?.page ?? "";
    if (!laborPageKeysEquivalent(page, jobPage)) return;
    await sleep(150);
  }
}

export function ClampQcReceivePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const closeShort = searchParams.get("closeShort") === "1";
  const startedRef = useRef(false);
  const { station, setStation, selectedPrinter, unavailableMessage, printersQuery } = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.clampQc,
    labelSize: "4x2",
    dpi: 300,
  });
  const [phase, setPhase] = useState<"working" | "done" | "error">("working");
  const [statusText, setStatusText] = useState("Leaving the job clock, then receiving.");
  const [errorText, setErrorText] = useState<string | null>(null);
  const [result, setResult] = useState<ClampQcFinishResponse | null>(null);
  const [containers, setContainers] = useState<ClampQcFailContainerPrint[]>([]);
  const [printError, setPrintError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [receiveSkipped, setReceiveSkipped] = useState(false);
  const autoPrintAttemptedRef = useRef(false);

  async function printContainers(printer: string): Promise<boolean> {
    const trimmed = printer.trim();
    if (trimmed === "") {
      setPrintError("Choose a printer to print the fail-pile containers.");
      return false;
    }
    setPrinting(true);
    setPrintError(null);
    try {
      const printed = await printClampQcFailContainers(slug, trimmed);
      setContainers(printed.containers);
      return printed.containers.length > 0;
    } catch (error) {
      setPrintError(error instanceof ApiError ? error.message : "Could not print fail-pile containers.");
      return false;
    } finally {
      setPrinting(false);
    }
  }

  useEffect(() => {
    if (startedRef.current || slug.trim() === "") return;
    startedRef.current = true;
    void (async () => {
      try {
        setStatusText("Waiting for this job clock to close.");
        await waitUntilJobClockIsClosed(slug);
        setStatusText("Receiving failed and passed units.");
        const outcome = await runClampQcReceiveOnce({ slug, closeShort });
        if (outcome.kind === "already_received") {
          setReceiveSkipped(true);
          setStatusText("Already received. Choose a printer to print fail-pile containers.");
        } else {
          setResult(outcome.result);
          setStatusText("Received. Choose a printer to print fail-pile containers.");
        }
        setPhase("done");
      } catch (error) {
        setPhase("error");
        setErrorText(error instanceof ApiError ? error.message : "Could not receive this work order.");
      }
    })();
  }, [closeShort, slug]);

  useEffect(() => {
    if (phase !== "done" || autoPrintAttemptedRef.current) return;
    const printer = selectedPrinter.trim();
    if (printer === "") return;
    autoPrintAttemptedRef.current = true;
    void printContainers(printer);
  }, [phase, selectedPrinter, slug]);

  const title = result?.workOrderLabel ?? slug;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp
        breadcrumbLabel={title}
        hoverHint="This page is not a job clock. Opening it clocks you out of the clamp QC job, then receives that order and prints one container label per fail pile on the damaged SKU."
      >
        {title}
      </PageTitleWithHelp>
      <p className="text-sm text-slate-700">{statusText}</p>
      {errorText != null ? <p className="text-sm text-rose-800">{errorText}</p> : null}
      {result != null ? (
        <p className="text-sm text-slate-800">
          {`Received ${String(result.receivedFailedQuantity)} failed and ${String(result.receivedPassedQuantity)} passed.`}
          {result.returnedQuantity > 0 ? ` Returned ${String(result.returnedQuantity)} unfinished.` : ""}
          {result.closed ? " Work order closed." : ""}
        </p>
      ) : null}
      {receiveSkipped ? (
        <p className="text-sm text-slate-800">
          Nothing left to receive on this work order. Print fail-pile container labels below if you still need them.
        </p>
      ) : null}
      <ZplPrinterListStatus printersQuery={printersQuery} />
      <ZplLocationSelect
        id="clamp-qc-receive-location"
        value={station}
        onChange={setStation}
        unavailableMessage={unavailableMessage}
        disabled={phase === "working"}
      />
      {containers.length > 0 ? (
        <ul className="list-disc space-y-1 pl-5 text-sm text-slate-800">
          {containers.map((container) => (
            <li key={container.pile}>
              {`${container.label}: ${String(container.quantity)} on ${container.sku}`}
            </li>
          ))}
        </ul>
      ) : null}
      {printError != null ? <p className="text-sm text-amber-900">{printError}</p> : null}
      {phase === "done" ? (
        <Button
          type="button"
          variant="secondary"
          disabled={printing || selectedPrinter.trim() === ""}
          onClick={() => void printContainers(selectedPrinter)}
        >
          {printing ? "Printing…" : "Print fail-pile containers"}
        </Button>
      ) : null}
    </div>
  );
}
